import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fab, Paper, Typography, IconButton, Stack, Box, Divider } from '@mui/material';
import {
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  MenuBook as GuideIcon,
  SupportAgent as ContactIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  AutoAwesome as VineIcon,
  ReceiptLong as BillingIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { CONTACT_URL } from '@/config/urls';
import { EMAIL } from '@/config/contacts';

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
  }
}

const CRISP_ID = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;
const MARKETING_CONTACT_URL = CONTACT_URL;

function loadCrisp(id: string): void {
  if (window.$crisp) return;
  window.$crisp = [];
  window.CRISP_WEBSITE_ID = id;
  const s = document.createElement('script');
  s.src = 'https://client.crisp.chat/l.js';
  s.async = true;
  document.head.appendChild(s);
}

/** One row in the launcher menu. */
function Row({ icon, label, sub, onClick, href, external }: {
  icon: React.ReactNode;
  label: string;
  sub?: string;
  onClick?: () => void;
  href?: string;
  external?: boolean;
}) {
  const inner = (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 1.5, py: 1.25, borderRadius: 1.5, cursor: 'pointer',
        transition: 'background 0.12s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold }}>{label}</Typography>
        {sub && <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>{sub}</Typography>}
      </Box>
      {external && <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
    </Stack>
  );
  if (href) {
    return (
      <Box component="a" href={href} target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        onClick={onClick} sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {inner}
      </Box>
    );
  }
  return <Box onClick={onClick}>{inner}</Box>;
}

/**
 * HelpLauncher — a single, Stripe-style help entry point that consolidates what
 * used to be three scattered controls: the "Talk to us" chat bubble, the footer
 * help icon, and the Help Center / User Guide menu links.
 *
 * One floating button (bottom-right, above the AI assistant) opens a menu: browse the help centre, read
 * the user guide, or contact support (email / request form). When Crisp is
 * configured (VITE_CRISP_WEBSITE_ID) its live-chat bubble takes over and this
 * renders nothing.
 */
export default function HelpLauncher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (CRISP_ID) loadCrisp(CRISP_ID);
  }, []);

  if (CRISP_ID) return null;

  const go = (path: string) => { setOpen(false); navigate(path); };

  return (
    <>
      <Fab
        aria-label="Help and support"
        onClick={() => setOpen((v) => !v)}
        size="medium"
        sx={{
          // Stacked directly above the AI Assistant FAB (56px tall at bottom: 24, right: 24)
          position: 'fixed', bottom: 92, right: 24,
          bgcolor: 'primary.main', color: 'primary.contrastText',
          zIndex: (t) => t.zIndex.drawer + 2, boxShadow: 4,
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        {open ? <CloseIcon /> : <HelpIcon />}
      </Fab>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 148, right: 24, width: 320,
            p: 1, borderRadius: 2.5, zIndex: (t) => t.zIndex.drawer + 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>How can we help?</Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close help">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Row icon={<SearchIcon />} label="Search help centre" sub="Guides, FAQs and how-tos" onClick={() => go('/help-center')} />
          <Row icon={<GuideIcon />} label="User guide" sub="Step-by-step product manual" onClick={() => go('/help')} />

          <Divider sx={{ my: 0.5 }} />

          <Row icon={<ContactIcon />} label="Contact support" sub="Submit a request — reply within 1 business day"
            href={MARKETING_CONTACT_URL} external onClick={() => setOpen(false)} />
          <Row icon={<EmailIcon />} label={`Email ${EMAIL.support}`}
            href={`mailto:${EMAIL.support}?subject=Support%20request`} onClick={() => setOpen(false)} />
          <Row icon={<BillingIcon />} label={`Billing: ${EMAIL.billing}`} sub="Receipts, invoices and payments"
            href={`mailto:${EMAIL.billing}?subject=Billing%20question`} onClick={() => setOpen(false)} />
          <Row icon={<VineIcon />} label={`Ask Vine: ${EMAIL.vine}`} sub="AI answers from our help articles"
            href={`mailto:${EMAIL.vine}`} onClick={() => setOpen(false)} />
        </Paper>
      )}
    </>
  );
}
